/**
 * MetenStartBundle — Option C (May 20 2026).
 *
 * Lage-drempel bundle page section combining:
 *   - 1× P1-meter (HomeWizard P1 by default; fallback to Youless P1)
 *   - 2× slimme meetstekkers (HomeWizard Energy Socket or Shelly Plug S Gen3)
 *   - Eigen Stroom Score (insight, part of EC abonnement)
 *   - EC abonnement · introductietarief € 9,95 / mnd
 *
 * Safety / commercial rules:
 *   - No fake affiliate URLs are rendered. Products are looked up from
 *     /api/energy-control/products and only show an outbound link when
 *     `affiliate_readiness === 'affiliate_ready'`. Otherwise the CTA
 *     softens to "Bekijk productinformatie" + lead-form.
 *   - Smart plugs are positioned ONLY for losse apparaten / inzicht,
 *     never for centrale automatische sturing.
 *   - EC v2 checkout flows via the central EnergyControlCheckoutButton,
 *     which respects the existing feature-flag and 503 handling.
 *
 * Funnel tracking (May 2026 — Option C tracking fix):
 *   The component fires internal funnel events via `trackFunnelEvent`
 *   (POSTs to /api/track/funnel, lands in funnel_events). Payload is
 *   restricted to product_id / product_name / affiliate_readiness /
 *   cta_label / cta_type / segment / intake_id. NEVER includes plain
 *   email or affiliate_url.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Gauge, Plug, Sparkles, ShieldCheck, ExternalLink } from 'lucide-react';
import EnergyControlCheckoutButton from '@/components/fase1/EnergyControlCheckoutButton';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { trackFunnelEvent } from '@/utils/tracking';

const API = '/api';
const SEGMENT = 'meten_start';

const PREFERRED_P1_IDS = ['homewizard_p1_meter', 'p1_dongle', 'youless_p1'];
const PREFERRED_PLUG_IDS = ['homewizard_energy_socket', 'shelly_plug_s_gen3', 'smart_plug_meter'];

function pickFirst(products, ids) {
  for (const pid of ids) {
    const p = products.find((x) => x.product_id === pid);
    if (p) return p;
  }
  return null;
}

function ProductTile({ product, role, onInquiry, testId, intakeId, onTrack }) {
  if (!product) {
    return (
      <div
        className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground"
        data-testid={`${testId}-missing`}
      >
        Productinformatie wordt nog samengesteld.
      </div>
    );
  }

  const readiness = product.affiliate_readiness || 'needs_affiliate_link';
  const canLinkOut = readiness === 'affiliate_ready' && product.affiliate_url;
  const cta = canLinkOut ? 'Bekijk product' : 'Bekijk productinformatie';

  // Shared safe meta payload — NEVER includes email or affiliate_url.
  const baseMeta = {
    section: 'meten_start_bundle',
    segment: SEGMENT,
    role,
    product_id: product.product_id,
    product_name: product.name,
    affiliate_readiness: readiness,
    intake_id: intakeId || '',
  };

  return (
    <div
      className="rounded-xl border border-border bg-white p-4 flex flex-col gap-2"
      data-testid={testId}
      data-affiliate-readiness={readiness}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-primary/80">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
          {role === 'p1' ? <Gauge className="w-3.5 h-3.5" /> : <Plug className="w-3.5 h-3.5" />}
        </span>
        {role === 'p1' ? 'P1-meter' : 'Slimme meetstekker'}
      </div>
      <h4 className="text-base font-semibold text-foreground leading-snug" data-testid={`${testId}-name`}>
        {product.name}
      </h4>
      {product.price_indication && (
        <div className="text-[13px] text-muted-foreground" data-testid={`${testId}-price`}>
          {product.price_indication}
        </div>
      )}
      {product.reason_short && (
        <p className="text-[13px] leading-relaxed text-foreground/80">{product.reason_short}</p>
      )}
      <div className="mt-1 flex flex-wrap gap-2">
        {canLinkOut ? (
          <a
            href={product.affiliate_url}
            target="_blank"
            rel="noopener noreferrer nofollow sponsored"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5"
            data-testid={`${testId}-link`}
            onClick={() => {
              onTrack({ ...baseMeta, cta_label: cta, cta_type: 'affiliate_link' });
            }}
          >
            {cta} <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <button
            type="button"
            onClick={() => {
              onTrack({ ...baseMeta, cta_label: cta, cta_type: 'product_info' });
              onInquiry?.(product.product_id);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5"
            data-testid={`${testId}-info`}
          >
            {cta}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            trackFunnelEvent('meten_start_add_to_bundle_clicked', {
              intake_id: intakeId || '',
              meta: { ...baseMeta, cta_label: 'Voeg toe aan mijn startpakket', cta_type: 'add_to_bundle' },
            });
            trackFunnelEvent('product_cta_clicked', {
              intake_id: intakeId || '',
              meta: { ...baseMeta, cta_label: 'Voeg toe aan mijn startpakket', cta_type: 'add_to_bundle' },
            });
            onInquiry?.(product.product_id);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/15"
          data-testid={`${testId}-add`}
        >
          Voeg toe aan mijn startpakket
        </button>
      </div>
    </div>
  );
}

export default function MetenStartBundle({
  email = null,                 // eslint-disable-line no-unused-vars  -- only used by checkout button below; NEVER passed to tracking
  intakeId = null,
  refCode = null,
  onInquiry,
}) {
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/energy-control/products`);
        const flat = [];
        for (const cat of res?.data?.categories || []) {
          for (const p of cat.products || []) flat.push(p);
        }
        if (!cancelled) setProducts(flat);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = !flagsLoading && loaded && flags.ec_v2_checkout_enabled;

  // Fire the "viewed" event exactly once when the bundle is actually rendered.
  useEffect(() => {
    if (!visible || viewedRef.current) return;
    viewedRef.current = true;
    trackFunnelEvent('meten_start_bundle_viewed', {
      intake_id: intakeId || '',
      meta: { section: 'meten_start_bundle', segment: SEGMENT, intake_id: intakeId || '' },
    });
  }, [visible, intakeId]);

  if (!visible) return null;

  // Tracker passed to tiles. Keeps the meta-shape uniform.
  const handleProductInfoTrack = (meta) => {
    trackFunnelEvent('meten_start_product_info_clicked', { intake_id: intakeId || '', meta });
    trackFunnelEvent('product_cta_clicked', { intake_id: intakeId || '', meta });
  };

  const p1 = pickFirst(products, PREFERRED_P1_IDS);
  const plug = pickFirst(products, PREFERRED_PLUG_IDS);

  return (
    <section
      className="rounded-2xl border-2 border-primary/30 bg-white p-5 sm:p-7"
      data-testid="meten-start-bundle"
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary mb-2">
        Lage drempel · meten start
      </div>
      <h2
        className="text-xl sm:text-2xl font-medium text-primary leading-snug"
        style={{ fontFamily: 'Outfit' }}
        data-testid="meten-start-title"
      >
        Meten Start pakket
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-foreground/85" data-testid="meten-start-copy">
        Begin met meten voordat u dure keuzes maakt. Powersure gebruikt P1-data en slimme meetproducten
        om uw eigen stroom, verbruik en beste volgende stap inzichtelijk te maken.
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="meten-start-tiles">
        <ProductTile product={p1}   role="p1"   onInquiry={onInquiry} testId="meten-start-p1"     intakeId={intakeId} onTrack={handleProductInfoTrack} />
        <ProductTile product={plug} role="plug" onInquiry={onInquiry} testId="meten-start-plug-1" intakeId={intakeId} onTrack={handleProductInfoTrack} />
        <ProductTile product={plug} role="plug" onInquiry={onInquiry} testId="meten-start-plug-2" intakeId={intakeId} onTrack={handleProductInfoTrack} />

        <div
          className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-2"
          data-testid="meten-start-insight"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold text-primary/80">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            Inzicht & abonnement
          </div>
          <h4 className="text-base font-semibold text-foreground leading-snug">
            Eigen Stroom Score + Energy Control
          </h4>
          <p className="text-[13px] leading-relaxed text-foreground/80">
            Powersure rekent uw P1-data + meetstekkers maandelijks door naar een Eigen Stroom Score
            en concrete bespaarkansen. Geen automatische sturing zonder uw toestemming.
          </p>
          <div
            className="mt-1"
            onClickCapture={() => {
              // The actual Stripe call + payment_clicked / checkout_started events
              // are still owned by EnergyControlCheckoutButton — we only add a
              // segment-scoped breadcrumb so we can attribute EC v2 starts that
              // came from the Meten Start tile vs. the standalone feature grid.
              trackFunnelEvent('meten_start_ec_checkout_clicked', {
                intake_id: intakeId || '',
                meta: {
                  section: 'meten_start_bundle',
                  segment: SEGMENT,
                  cta_label: 'Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95',
                  cta_type: 'ec_intro_checkout',
                  price: 9.95,
                  intake_id: intakeId || '',
                },
              });
            }}
          >
            <EnergyControlCheckoutButton
              enabled
              email={email}
              intakeId={intakeId}
              refCode={refCode}
              label="Start Energy Control · € 9,95 eerste 3 mnd · daarna € 14,95"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="meten-start-secondary-ctas">
        <button
          type="button"
          onClick={() => {
            trackFunnelEvent('meten_start_installer_help_clicked', {
              intake_id: intakeId || '',
              meta: {
                section: 'meten_start_bundle',
                segment: SEGMENT,
                cta_label: 'Installatiehulp nodig?',
                cta_type: 'installer_help',
                intake_id: intakeId || '',
              },
            });
            onInquiry?.('installer_call');
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold border-2 border-primary text-primary bg-white hover:bg-primary/5"
          data-testid="meten-start-installer-help"
        >
          <ShieldCheck className="w-4 h-4" />
          Installatiehulp nodig?
        </button>
      </div>
    </section>
  );
}
